import PropTypes from 'prop-types';
import { forwardRef } from 'react';
import styled, { css } from 'styled-components';

import { theme } from '_constants/theme';

import Button from '_components/atomic/Button';

const { devices, spaces } = theme;

// eslint-disable-next-line prettier/prettier, react/display-name
const StyledContainer = styled(forwardRef(({ color, innerSpace, notRoundedMobile, roundingRadius, ...props }, ref) => <div ref={ref} {...props} />))`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;

  border-radius: ${spaces.regular}px;

  ${({ color }) =>
    color &&
    css`
      background: ${color?.gradient ? color.gradient?.from : color};
    `}

  ${({ innerSpace }) =>
    innerSpace &&
    css`
      padding: ${typeof innerSpace === 'number' ? innerSpace : spaces.medium}px;
    `}

  ${({ roundingRadius }) =>
    roundingRadius &&
    css`
      border-radius: ${roundingRadius}px;
    `}

  @media ${devices.tablet} {
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;

    padding: ${spaces.regular}px;

    ${({ notRoundedMobile }) =>
      notRoundedMobile &&
      css`
        border-radius: 0;
      `}
  }
`;

const StyledContent = styled.div`
  @media ${devices.tablet} {
    width: 100%;
  }
`;

const StyledTitle = styled.div``;

const StyledBody = styled.div``;
const StyledFooter = styled.div`
  @media ${devices.tablet} {
    display: none;
  }
`;
const StyledFooterMobile = styled.div`
  display: none;
  @media ${devices.tablet} {
    display: block;
  }
`;

// eslint-disable-next-line prettier/prettier, react/display-name
const StyledBadge = styled(forwardRef(({ color, roundingRadius, ...props }, ref) => <div ref={ref} {...props} />))`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: center;

  padding: ${spaces.regular}px;
  border-radius: ${spaces.small - 2}px;

  ${({ color }) =>
    color &&
    css`
      background: ${color?.gradient
        ? `linear-gradient(0deg, ${color.gradient?.from} 0%, ${color.gradient?.to} 100%)`
        : color};
    `}

  ${({ roundingRadius }) =>
    roundingRadius &&
    css`
      border-radius: ${roundingRadius}px;
    `}

  @media ${devices.tablet} {
    width: 100%;
  }
`;

const StyledBadgeText = styled.div``;

const StyledBadgeSubtext = styled.div``;

const StyledButtonContainer = styled.div`
  margin-top: ${spaces.small}px;
`;

const PriceBanner = ({
  children,
  innerSpace,
  roundingRadius,
  notRoundedMobile,
  title,
  badgeText,
  badgeSubtext,
  badgeRoundingRadius,
  color,
  footer,
  buttons = [],
}) => {
  return (
    <StyledContainer
      color={color}
      innerSpace={innerSpace}
      notRoundedMobile={notRoundedMobile}
      roundingRadius={roundingRadius}
    >
      <StyledContent>
        <StyledTitle>{title}</StyledTitle>
        <StyledBody>{children}</StyledBody>
        <StyledFooter>{footer}</StyledFooter>
      </StyledContent>
      <StyledBadge color={color} roundingRadius={badgeRoundingRadius}>
        <StyledBadgeText>{badgeText}</StyledBadgeText>
        <StyledBadgeSubtext>{badgeSubtext}</StyledBadgeSubtext>
        {!!buttons?.length &&
          buttons.map(({ text, ...btnProps }, idx) => (
            <StyledButtonContainer key={`btn:${idx}`}>
              <Button {...btnProps}>{text}</Button>
            </StyledButtonContainer>
          ))}
      </StyledBadge>
      <StyledFooterMobile>{footer}</StyledFooterMobile>
    </StyledContainer>
  );
};

PriceBanner.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]),
  innerSpace: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  notRoundedMobile: PropTypes.bool,
  roundingRadius: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  title: PropTypes.node,
  badgeText: PropTypes.node,
  badgeSubtext: PropTypes.node,
  badgeRoundingRadius: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  color: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  buttons: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.node,
      color: PropTypes.string,
      disabled: PropTypes.bool,
      large: PropTypes.bool,
      transparent: PropTypes.bool,
      outlined: PropTypes.bool,
      prefix: PropTypes.node,
      suffix: PropTypes.node,
      secondary: PropTypes.bool,
      onClick: PropTypes.func,
    })
  ),
};

export default PriceBanner;
